/** @type {{ onSessionInvalidated?: (message: string) => void } | null} */
let handlers = null;

let suppressUntil = 0;

export function registerAuthSessionHandlers(next) {
  handlers = next && typeof next === 'object' ? next : null;
}

export function flushAuthSessionHandlers() {
  handlers = null;
}

/** Call after successful manual login so parallel failures do not immediately kick the user out. */
export function suppressSessionInvalidation(ms = 800) {
  suppressUntil = Date.now() + ms;
}

function isSuppressed() {
  return Date.now() < suppressUntil;
}

function messageFromPayload(data) {
  if (!data || typeof data !== 'object') return '';
  return String(data.message ?? data.error ?? '').trim();
}

/**
 * True when API indicates the JWT/session is no longer valid.
 * Avoid matching generic validation errors (e.g. wrong password on login).
 */
export function isAuthSessionInvalidPayload(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.success !== false) return false;
  const m = messageFromPayload(data).toLowerCase();
  if (!m) return false;
  return (
    m.includes('please login') ||
    m.includes('login first') ||
    m.includes('session expired') ||
    m.includes('token expired') ||
    m.includes('invalid token') ||
    m.includes('jwt expired') ||
    m.includes('signature has expired') ||
    m.includes('not authenticated') ||
    m.includes('authentication required') ||
    m === 'unauthorized' ||
    m.startsWith('unauthorized')
  );
}

function shouldInvalidateSession(result, api) {
  if (api?.endpoint === 'login') return false;
  if (isSuppressed()) return false;

  if (result?.error) {
    const status = result.error.status ?? result.error.originalStatus;
    if (status === 401 || status === 403) return true;
    const body = result.error.data;
    if (isAuthSessionInvalidPayload(body)) return true;
    return false;
  }

  if (result?.data && typeof result.data === 'object' && isAuthSessionInvalidPayload(result.data)) {
    return true;
  }
  return false;
}

let lastInvalidationAt = 0;
const INVALIDATION_DEBOUNCE_MS = 2500;

function triggerSessionInvalidated(message) {
  if (isSuppressed()) return;
  const now = Date.now();
  if (now - lastInvalidationAt < INVALIDATION_DEBOUNCE_MS) return;
  lastInvalidationAt = now;
  const fn = handlers?.onSessionInvalidated;
  if (typeof fn !== 'function') return;
  try {
    fn(message || 'Please login again.');
  } catch {
    /* ignore */
  }
}

/** Non-RTK `fetch` wrappers (e.g. file download) after HTTP 401/403. */
export function reportAuthSessionLost(message) {
  triggerSessionInvalidated(message || 'Please login again.');
}

/**
 * RTK Query baseQuery wrapper: normalize "soft" auth failures to errors and clear session once.
 * @param {*} result
 * @param {*} api
 */
export function handleApiResultForSession(result, api) {
  if (!shouldInvalidateSession(result, api)) return result;

  const msg =
    messageFromPayload(result?.data) ||
    messageFromPayload(result?.error?.data) ||
    'Session expired. Please login again.';

  triggerSessionInvalidated(msg);

  if (result.error) return result;

  return {
    error: {
      status: 401,
      data: result.data ?? { success: false, message: msg },
    },
    meta: result.meta,
  };
}
